import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Edit,
  Trash2,
  Palette,
  Sparkles,
  RefreshCw,
  AlertCircle,
  X,
  Check,
} from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCoreValuesAdmin,
  useCreateCoreValue,
  useUpdateCoreValue,
  useDeleteCoreValue,
  CoreValue,
  CoreValueTranslation,
} from "@/services/coreValueService";

const CoreValues: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  // API hooks - use admin endpoint to get translations
  const {
    data: coreValuesResponse,
    isLoading,
    error,
    refetch,
  } = useCoreValuesAdmin({
    locale: currentLanguage,
  });

  const createCoreValueMutation = useCreateCoreValue();
  const updateCoreValueMutation = useUpdateCoreValue();
  const deleteCoreValueMutation = useDeleteCoreValue();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingValue, setEditingValue] = useState<CoreValue | null>(null);
  const [editingTranslations, setEditingTranslations] = useState<
    CoreValueTranslation[]
  >([]);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingDescription, setEditingDescription] = useState("");
  const [newValue, setNewValue] = useState({
    title: "",
    description: "",
    icon: "",
    color: "",
    gradient: "",
  });

  // Load core values when available
  useEffect(() => {
    // Data is handled by the API hook
  }, [coreValuesResponse]);

  const handleCreate = async () => {
    if (newValue.title.trim() && newValue.description.trim()) {
      try {
        const translations = [
          {
            locale: currentLanguage,
            title: newValue.title.trim(),
            description: newValue.description.trim(),
          },
        ];

        await createCoreValueMutation.mutateAsync({
          icon: newValue.icon || undefined,
          color: newValue.color || undefined,
          gradient: newValue.gradient || undefined,
          translations,
        });

        setNewValue({
          title: "",
          description: "",
          icon: "",
          color: "",
          gradient: "",
        });
        setIsCreateDialogOpen(false);
      } catch (error) {
        console.error("Error creating core value:", error);
      }
    }
  };

  const handleEdit = (value: CoreValue) => {
    setEditingValue(value);
    setEditingTranslations(value.translations || []);
    const currentTranslation = value.translations?.find(
      (t) => t.locale === currentLanguage
    );
    setEditingTitle(currentTranslation?.title || value.title || "");
    setEditingDescription(
      currentTranslation?.description || value.description || ""
    );
  };

  const handleCancelEdit = () => {
    setEditingValue(null);
    setEditingTitle("");
    setEditingDescription("");
    setEditingTranslations([]);
  };

  const handleSaveEdit = async () => {
    if (editingValue) {
      try {
        const translations = editingTranslations.map((t) => ({
          locale: t.locale,
          title: t.locale === currentLanguage ? editingTitle : t.title,
          description:
            t.locale === currentLanguage
              ? editingDescription
              : t.description || "",
        }));

        // If no translation exists for current language, add it
        if (
          !translations.find((t) => t.locale === currentLanguage) &&
          editingTitle.trim()
        ) {
          translations.push({
            locale: currentLanguage,
            title: editingTitle.trim(),
            description: editingDescription.trim(),
          });
        }

        await updateCoreValueMutation.mutateAsync({
          id: editingValue.id,
          coreValueData: {
            icon: editingValue.icon,
            color: editingValue.color,
            gradient: editingValue.gradient,
            translations,
          },
        });

        handleCancelEdit();
      } catch (error) {
        console.error("Error updating core value:", error);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t("admin.core_values.confirm_delete"))) {
      try {
        await deleteCoreValueMutation.mutateAsync(id);
      } catch (error) {
        console.error("Error deleting core value:", error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              {t("sidebar.core_values")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t("admin.core_values.subtitle")}
            </p>
          </div>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <Plus className="w-4 h-4" />
            {t("admin.core_values.add")}
          </Button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <Card
                key={index}
                className="relative bg-white dark:bg-gray-800 border-0 shadow-md animate-pulse"
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
                    </div>
                    <div className="flex gap-1">
                      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Card className="bg-white dark:bg-gray-800 border-red-200 dark:border-red-900">
            <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
              <AlertCircle className="w-12 h-12 text-red-500" />
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t("common.error_loading_data")}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-md">
                  {t("admin.core_values.error_loading")}
                </p>
              </div>
              <Button
                onClick={() => refetch()}
                className="flex items-center gap-2"
                variant="outline"
              >
                <RefreshCw className="w-4 h-4" />
                {t("common.retry")}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Core Values Grid */}
        {!isLoading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreValuesResponse?.core_values?.map((value) => (
              <Card
                key={value.id}
                className="relative bg-white dark:bg-gray-800 border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                {/* Gradient accent */}
                {value.gradient && (
                  <div
                    className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${value.gradient}`}
                  />
                )}

                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    {editingValue?.id === value.id ? (
                      <Input
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        className="flex-1 mr-2 font-semibold"
                        placeholder="Title"
                      />
                    ) : (
                      <div className="flex items-center gap-3 flex-1">
                        {value.icon && (
                          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                            <span className="text-2xl">{value.icon}</span>
                          </div>
                        )}
                        <span className="font-semibold text-gray-900 dark:text-white truncate">
                          {value.translations?.find(
                            (t) => t.locale === currentLanguage
                          )?.title ||
                            value.title ||
                            `Core Value ${value.id}`}
                        </span>
                      </div>
                    )}
                    <div className="flex gap-1">
                      {editingValue?.id === value.id ? (
                        <>
                          <Button
                            size="sm"
                            onClick={handleSaveEdit}
                            disabled={updateCoreValueMutation.isPending}
                            className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleCancelEdit}
                            className="h-8 w-8 p-0"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(value)}
                            className="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(value.id)}
                            disabled={deleteCoreValueMutation.isPending}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {editingValue?.id === value.id ? (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor={`edit-icon-${value.id}`}>Icon</Label>
                        <Input
                          id={`edit-icon-${value.id}`}
                          value={editingValue.icon || ""}
                          onChange={(e) =>
                            setEditingValue({
                              ...editingValue,
                              icon: e.target.value,
                            })
                          }
                          placeholder="Icon (emoji or class)"
                          className="mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor={`edit-color-${value.id}`}>
                            Color
                          </Label>
                          <Input
                            id={`edit-color-${value.id}`}
                            value={editingValue.color || ""}
                            onChange={(e) =>
                              setEditingValue({
                                ...editingValue,
                                color: e.target.value,
                              })
                            }
                            placeholder="Color"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor={`edit-gradient-${value.id}`}>
                            Gradient
                          </Label>
                          <Input
                            id={`edit-gradient-${value.id}`}
                            value={editingValue.gradient || ""}
                            onChange={(e) =>
                              setEditingValue({
                                ...editingValue,
                                gradient: e.target.value,
                              })
                            }
                            placeholder="Gradient"
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor={`edit-description-${value.id}`}>
                          Description
                        </Label>
                        <Textarea
                          id={`edit-description-${value.id}`}
                          value={editingDescription}
                          onChange={(e) =>
                            setEditingDescription(e.target.value)
                          }
                          rows={4}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(value.color || value.gradient) && (
                        <div className="flex flex-wrap gap-2">
                          {value.color && (
                            <Badge
                              variant="secondary"
                              className="flex items-center gap-1"
                            >
                              <Palette className="w-3 h-3" />
                              {value.color}
                            </Badge>
                          )}
                          {value.gradient && (
                            <Badge
                              variant="secondary"
                              className="flex items-center gap-1"
                            >
                              <Sparkles className="w-3 h-3" />
                              Gradient
                            </Badge>
                          )}
                        </div>
                      )}
                      <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                        {value.translations?.find(
                          (t) => t.locale === currentLanguage
                        )?.description ||
                          value.description ||
                          "No description"}
                      </p>
                      {value.translations && value.translations.length > 1 && (
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Available in {value.translations.length} languages
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading &&
          !error &&
          coreValuesResponse?.core_values?.length === 0 && (
            <Card className="bg-white dark:bg-gray-800">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Sparkles className="w-16 h-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  No Core Values
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Get started by creating your first core value.
                </p>
                <Button
                  onClick={() => setIsCreateDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Core Value
                </Button>
              </CardContent>
            </Card>
          )}

        {/* Create Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {t("admin.core_values.add_new")}
              </DialogTitle>
              <DialogDescription>
                {t("admin.core_values.add_description")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="create-title">
                  {t("admin.core_values.title")} *
                </Label>
                <Input
                  id="create-title"
                  value={newValue.title}
                  onChange={(e) =>
                    setNewValue({ ...newValue, title: e.target.value })
                  }
                  placeholder={t("admin.core_values.title_placeholder")}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="create-description">
                  {t("admin.core_values.description")} *
                </Label>
                <Textarea
                  id="create-description"
                  value={newValue.description}
                  onChange={(e) =>
                    setNewValue({ ...newValue, description: e.target.value })
                  }
                  rows={4}
                  placeholder={t("admin.core_values.description_placeholder")}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="create-icon">Icon</Label>
                <Input
                  id="create-icon"
                  value={newValue.icon}
                  onChange={(e) =>
                    setNewValue({ ...newValue, icon: e.target.value })
                  }
                  placeholder="Icon (emoji or CSS class)"
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="create-color">Color</Label>
                  <Input
                    id="create-color"
                    value={newValue.color}
                    onChange={(e) =>
                      setNewValue({ ...newValue, color: e.target.value })
                    }
                    placeholder="Color (e.g., text-blue-600)"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="create-gradient">Gradient</Label>
                  <Input
                    id="create-gradient"
                    value={newValue.gradient}
                    onChange={(e) =>
                      setNewValue({ ...newValue, gradient: e.target.value })
                    }
                    placeholder="Gradient (e.g., from-blue-500 to-cyan-600)"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleCreate}
                disabled={
                  !newValue.title.trim() ||
                  !newValue.description.trim() ||
                  createCoreValueMutation.isPending
                }
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
              >
                {createCoreValueMutation.isPending
                  ? t("common.creating")
                  : t("admin.core_values.create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

CoreValues.displayName = "CoreValues";

export default CoreValues;
